/**
 * AppAccessSheet — the ONE shared access sheet (2026-08-16,
 * PLAN-connected-apps-governance-20260816 §6: one component, four surfaces).
 *
 * Renders the three tier PRESETS (identical rows to the inline tier menu it
 * replaces on the Integrations panel) plus a collapsible "fine-tune" section
 * with one checkbox per capability class (read / create / send / delete /
 * admin). Mounted today inside the panel's anchored popover; the Team & Roles
 * role editor mounts the same component later (role mode is a follow-up PR).
 *
 * Contract notes:
 *  - Read is structural: checked + disabled always — disconnecting the app is
 *    the only way to remove read access.
 *  - Zero-count class rows are hidden ENTIRELY when live counts are present
 *    (Drive has no true Send tools, so Drive shows no Send row); a null
 *    classCounts (pre-capability-map backend) shows all rows, count-less.
 *  - A stored profile matching no preset is "Custom": presets render
 *    unselected and the fine-tune section auto-expands so the state is
 *    always explainable.
 *  - Members without canManage see every control disabled with the existing
 *    "Ask an owner or admin" tooltip pattern — never a hidden section.
 */

import React from 'react';
import panelTheme from './panelTheme';
import {
  ACCESS_TIER_LABELS,
  ACCESS_TIER_DESCRIPTIONS,
  CAPABILITY_CLASS_LABELS,
  type AccessTier,
  type AccessTierInfo,
  type AccessProfile,
  type CapabilityClass,
} from './ComposioClient';
import { CLASS_ORDER, useAppAccessSheet } from './useAppAccessSheet';

export interface AppAccessSheetProps {
  slug: string;
  displayName?: string;
  access: AccessTierInfo | null;
  classCounts: Record<CapabilityClass, number> | null;
  canManage: boolean;
  saving: boolean;
  onSelectTier: (tier: AccessTier) => void;
  onSaveProfile: (profile: AccessProfile) => void;
  /** Per-class example verbs from the live capability map (2026-08-18 —
   *  fetched by the panel since the governance PR, now finally rendered).
   *  Null/absent → no examples line (tail apps have no map). */
  classExamples?: Record<CapabilityClass, string[]> | null;
  /** One-line per-class descriptions (CAPABILITY_CLASS_SUMMARIES). Absent →
   *  labels only (the compact popover contexts). */
  classSummaries?: Record<CapabilityClass, string> | null;
  /** Storage apps: the folder-scope row ("▸ folders: 3 chosen [change]") —
   *  plan §6's one-sheet promise. Absent → no row. */
  folderScopeRow?: { label: string; onChange: () => void } | null;
  /** The access fetch for this open FAILED (2026-08-25): say so honestly —
   *  "loading access…" forever over dead controls was the old lie. Closing
   *  and reopening the sheet retries. */
  loadFailed?: boolean;
}

const READ_LOCK_TOOLTIP =
  'reading is part of being connected — disconnect the app to remove all access';

export const AppAccessSheet: React.FC<AppAccessSheetProps> = ({
  slug,
  displayName,
  access,
  classCounts,
  canManage,
  saving,
  onSelectTier,
  onSaveProfile,
  classExamples,
  classSummaries,
  folderScopeRow,
  loadFailed,
}) => {
  // What the sheet does is ./useAppAccessSheet.ts's (PV14 part 2a); this draws it.
  const { profile, isCustom, selectedTier, interactive, fineTuneOpen, toggleFineTune, toggleClass } = useAppAccessSheet({ access, canManage, saving, onSaveProfile });

  return (
    <div data-testid={`app-access-sheet-${slug}`}>
      {/* Honest loading line (2026-08-24 WhatsApp repro): a null `access`
          used to render every control silently disabled with no explanation
          — a dead sheet. Say what is happening while detail loads. */}
      {!access && (
        <div
          data-testid={`access-sheet-loading-${slug}`}
          style={{ color: panelTheme.grayText, fontSize: '0.8rem', marginBottom: 10, textTransform: 'lowercase' }}
        >
          {loadFailed
            ? 'couldn’t load access details — close and reopen to retry'
            : 'loading access…'}
        </div>
      )}
      {/* ── Presets — byte-for-byte the rows of the inline tier menu this
          sheet replaced (gold selection, ✓ mark, plain-verbs copy). */}
      {(['full', 'read-write', 'read-only'] as AccessTier[]).map((tier) => (
        <button
          key={tier}
          onClick={() => { if (interactive) onSelectTier(tier); }}
          disabled={!interactive}
          title={!canManage ? 'Ask an owner or admin' : undefined}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: selectedTier === tier ? 'rgba(198, 167, 94, 0.14)' : 'none',
            border: selectedTier === tier ? '1px solid var(--brand-gold)' : '1px solid transparent',
            borderRadius: '8px', padding: '0.4rem 0.5rem',
            cursor: saving ? 'wait' : canManage ? 'pointer' : 'not-allowed',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <span style={{ display: 'block', fontWeight: 600, fontSize: '0.78rem', color: '#111827' }}>
            {selectedTier === tier ? '✓ ' : ''}{ACCESS_TIER_LABELS[tier]}
          </span>
          <span style={{ display: 'block', fontSize: '0.72rem', color: '#6B7280', lineHeight: 1.4 }}>
            {ACCESS_TIER_DESCRIPTIONS[tier]}
          </span>
        </button>
      ))}

      <div style={{ borderTop: '1px solid #E7DCC3', margin: '0.35rem 0.2rem' }} />

      {/* ── Fine-tune expander — collapsed by default; the presets stay the
          zero-decision path (§10). */}
      <button
        onClick={toggleFineTune}
        style={{
          display: 'block', width: '100%', textAlign: 'left',
          background: 'none', border: 'none', borderRadius: '8px',
          padding: '0.35rem 0.5rem', cursor: 'pointer',
          fontSize: '0.74rem', fontWeight: 600, color: '#6b5d3f',
          textTransform: 'lowercase',
        }}
      >
        {fineTuneOpen ? '▾' : '▸'} fine-tune{isCustom ? ' — custom' : ''}
      </button>

      {fineTuneOpen && (
        <div>
          {displayName && (
            <div style={{
              fontSize: '0.68rem', color: '#9CA3AF', padding: '0 0.5rem 0.25rem',
            }}>
              what Prest0n may do in {displayName}
            </div>
          )}
          {CLASS_ORDER.map((cls) => {
            const count = classCounts ? classCounts[cls] ?? 0 : null;
            // Live counts present + zero tools in this class → the row would
            // govern nothing; hide it entirely (§6: Drive shows no Send row).
            if (count === 0) return null;
            const isRead = cls === 'read';
            const checked = isRead ? true : profile[cls];
            const disabled = isRead || !interactive;
            return (
              <label
                key={cls}
                title={isRead ? READ_LOCK_TOOLTIP : !canManage ? 'Ask an owner or admin' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.45rem',
                  padding: '0.3rem 0.5rem', borderRadius: '8px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleClass(cls)}
                  aria-label={CAPABILITY_CLASS_LABELS[cls]}
                  style={{ accentColor: 'var(--brand-gold)', margin: 0 }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#111827' }}>
                    {CAPABILITY_CLASS_LABELS[cls]}
                  </span>
                  {/* One-line summary + real example verbs (2026-08-18):
                      the sheet finally SHOWS what each class means for THIS
                      app — the data was already on the wire. */}
                  {classSummaries?.[cls] && (
                    <span style={{ display: 'block', fontSize: '0.68rem', color: '#6B7280', lineHeight: 1.4 }}>
                      {classSummaries[cls]}
                    </span>
                  )}
                  {(classExamples?.[cls]?.length ?? 0) > 0 && (
                    <span
                      data-testid={`access-examples-${cls}`}
                      style={{ display: 'block', fontSize: '0.66rem', color: '#9CA3AF', lineHeight: 1.4 }}
                    >
                      e.g. {classExamples![cls].slice(0, 3).join(', ')}
                    </span>
                  )}
                </span>
                {count !== null && (
                  <span style={{
                    fontSize: '0.66rem', fontWeight: 600, color: '#6b5d3f',
                    background: 'rgba(198, 167, 94, 0.15)', borderRadius: '10px',
                    padding: '0.08rem 0.4rem', whiteSpace: 'nowrap',
                  }}>
                    {count} tools
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {/* Storage apps: the folder question lives ON the sheet (plan §6 —
          "▸ folders: N chosen [change]"), one tap to the picker. */}
      {folderScopeRow && (
        <>
          <div style={{ borderTop: '1px solid #E7DCC3', margin: '0.35rem 0.2rem' }} />
          <button
            onClick={folderScopeRow.onChange}
            data-testid={`access-folder-row-${slug}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%',
              background: 'none', border: 'none', borderRadius: '8px',
              padding: '0.35rem 0.5rem', cursor: 'pointer', textAlign: 'left',
              fontSize: '0.74rem', fontWeight: 600, color: '#6b5d3f',
              textTransform: 'lowercase',
            }}
          >
            <span aria-hidden="true">📁</span>
            <span style={{ flex: 1 }}>{folderScopeRow.label}</span>
            <span style={{ textDecoration: 'underline' }}>change</span>
          </button>
        </>
      )}
    </div>
  );
};

export default AppAccessSheet;
